/*
  nf-gift-card (sections/nf-gift-card.liquid): the Format switch, the amounts, the live handwriting and the pictures.
  Adding to the basket is Avante's <product-form> (assets/product-form.js); this file only sets which variant it sends,
  switches off the fields of the format not chosen, and checks the recipient email first.
  Pictures (owner 20.10): E-gift = the variant's own flat image; Printed = the design app's 3D card, its scene code
  ported below as it is in the app (thickness, perspective, tilt with spring, glint, turn).
*/
(() => {
  if (customElements.get('nf-gift-card')) return;

  /* ==================================================================== the app's card in the hand
     Ported from the design app (shopify_gift_cards/local_7a95136f-…/outputs/netherfield_gift_card.html, lines
     3132-3382: POSE, poseEdge, poseWatch, sceneOn, liveAll, makeLean). The code is the app's; changed only: class
     names (.flipdeck → .nfc-flipdeck), liveAll takes the one card on this page instead of the app's board, and
     the turn writes the section's data-side instead of the app's state. The app's comments are kept short here;
     the full reasoning is in the app. */
  const POSE = new WeakMap();          // tiltbox -> { hover }

  /* How far the shown face is turned away from the viewer, read off the composed matrix (m33 is the cosine of
     the face normal with the line of sight); its sine is how much edge shows, and that is the edge's opacity. */
  function poseEdge(lean){
    if(!lean.getClientRects().length) return { mag: 0, arrived: true };
    const deck = lean.querySelector('.nfc-flipdeck');
    const M = m => new DOMMatrix(m === 'none' ? '' : m);
    let m = M(getComputedStyle(lean).transform), arrived = true;
    if(deck){
      const cs = getComputedStyle(deck);
      const dm = M(cs.transform);
      m = m.multiply(dm);
      /* the turn is over when the deck has reached the angle asked of it */
      const target = Math.abs((parseFloat(cs.getPropertyValue('--flip')) || 0) % 360);
      const now = Math.acos(Math.max(-1, Math.min(1, dm.m11))) * 180 / Math.PI;
      arrived = Math.abs(now - target) < 0.5;
    }
    const mag = Math.sqrt(Math.max(0, 1 - m.m33 * m.m33));
    lean.style.setProperty('--tiltmag', mag.toFixed(3));
    lean.classList.toggle('tilted', mag > 0.02);
    return { mag, arrived };
  }

  /* The loop: runs while the pointer is on the card, the deck has not reached its angle, or the lean has not
     returned to nil; stops when none holds, which is also the moment the 3D scene is dropped. */
  function poseWatch(lean){
    if(!lean || lean.dataset.posing) return;
    lean.dataset.posing = '1';
    const step = () => {
      const state = POSE.get(lean) || {};
      const hover = state.hover && lean.getClientRects().length > 0;
      if(hover && lean.classList.contains('tracking')){
        if(!lean.classList.contains('turning')){
          requestAnimationFrame(step);
          return;
        }
      }
      const { mag, arrived } = poseEdge(lean);
      if(!arrived) lean.classList.add('turning');
      else lean.classList.remove('turning');
      if(hover || !arrived || mag > 0.005){ requestAnimationFrame(step); return; }
      delete lean.dataset.posing;
      lean.classList.remove('live', 'turning');
      lean.style.setProperty('--tiltmag', '0');
      lean.classList.remove('tilted');
    };
    requestAnimationFrame(step);
  }

  /* the scene goes on the moment any of the three begins; the loop decides when it comes off */
  function sceneOn(lean, hovering){
    if(!lean) return;
    const state = POSE.get(lean) || {};
    if(hovering !== undefined) state.hover = hovering ? 1 : 0;
    POSE.set(lean, state);
    lean.classList.add('live');
    poseWatch(lean);
  }

  /* the card is given the scene before its angle changes, so the turn animates in 3D (app: every card on its board) */
  function liveAll(lean){
    const boxes = [lean].filter(Boolean);
    for(const el of boxes) el.classList.add('live');
    void (lean || document.body).offsetWidth;                  // stand up in 3D first, unanimated
    for(const el of boxes) el.classList.add('turning');
    void (lean || document.body).offsetWidth;                  // arm the transition
    for(const el of boxes) sceneOn(el);
  }

  function makeLean({ hit, lean, faces, maxTilt = 16, lift = 0.038, pick = 1.035, onClick }){
    const still = matchMedia('(prefers-reduced-motion: reduce)');
    /* A finger has no hover: on a touch screen the lean follows a drag and springs back when it lifts; a tap
       that did not travel turns the card over. */
    const isMouse = e => e.pointerType === undefined || e.pointerType === 'mouse';
    let dragging = false, moved = 0, start = null, byTouch = false;
    const LAMP = { x: -0.30, y: -0.55 };   // where the lamp is, in lean space
    const EASE = 0.20;                     // how fast it catches up with the pointer

    // written once per animation frame from one smoothed position
    let want = { x:0, y:0, on:0 }, at = { x:0, y:0, on:0 }, frame = 0;

    function paint(){
      const gap = Math.abs(want.x - at.x) + Math.abs(want.y - at.y) + Math.abs(want.on - at.on);
      at.x  += (want.x  - at.x)  * EASE;
      at.y  += (want.y  - at.y)  * EASE;
      at.on += (want.on - at.on) * EASE;

      const w = hit.clientWidth || 1;          // the card's drawn width, this frame
      lean.style.setProperty('--ty',   (at.x * maxTilt).toFixed(2) + 'deg');
      lean.style.setProperty('--tx',   (-at.y * maxTilt).toFixed(2) + 'deg');
      lean.style.setProperty('--lift', (at.on * lift * w).toFixed(1) + 'px');
      lean.style.setProperty('--pick', (1 + at.on * (pick - 1)).toFixed(4));
      lean.style.setProperty('--glint', at.on.toFixed(3));

      // the shadow is thrown by the edge that has come towards the viewer
      lean.style.setProperty('--sh-x', (-at.x * 0.038 * w).toFixed(1));
      lean.style.setProperty('--sh-y', ((0.026 - at.y * 0.026) * w).toFixed(1));
      lean.style.setProperty('--sh-b', (at.on * 0.035 * w).toFixed(1));
      lean.style.setProperty('--sh-a', at.on.toFixed(3));

      const mag = Math.min(1, Math.hypot(at.x, at.y));
      lean.style.setProperty('--tiltmag', mag.toFixed(3));
      lean.classList.toggle('tilted', mag > 0.02);
      const d = Math.hypot(at.x - LAMP.x, at.y - LAMP.y);
      const spec = Math.exp(-(d * d) / (2 * 0.42 * 0.42));

      // a face turned through 180 degrees has its x axis running backwards on screen: its light is mirrored
      for(const face of faces){
        face.el.style.setProperty('--glint-gain', byTouch ? '.45' : '1');
        const dir = Math.atan2(at.y, face.sign * at.x) * 180 / Math.PI;
        face.el.style.setProperty('--rake', (90 + dir).toFixed(1) + 'deg');
        face.el.style.setProperty('--spec-ang', dir.toFixed(1) + 'deg');
        face.el.style.setProperty('--wash', (mag * 0.34).toFixed(3));
        face.el.style.setProperty('--spec', (spec * mag).toFixed(3));
      }

      if(gap > 0.001) frame = requestAnimationFrame(paint);
      else { frame = 0; if(!want.on) lean.classList.remove('tracking'); }
    }

    // the pointer is tracked on the still box, never on the leaning one
    hit.addEventListener('pointermove', e => {
      if(still.matches) return;
      if(!isMouse(e) && !dragging) return;
      if(hit.closest('figure')?.style.pointerEvents === 'none') return;
      const r = hit.getBoundingClientRect();
      const clamp = v => v < -1 ? -1 : v > 1 ? 1 : v;
      want.x = clamp((((e.clientX - r.left) / r.width) - .5) * 2);   // -1 left edge, +1 right
      want.y = clamp((((e.clientY - r.top) / r.height) - .5) * 2);
      want.on = 1;
      lean.classList.add('tracking');
      sceneOn(lean, true);
      if(!frame) frame = requestAnimationFrame(paint);
    });

    // letting go hands the movement back to the CSS easing, which has a small overshoot
    hit.addEventListener('pointerleave', () => {
      sceneOn(lean, false);
      want.x = want.y = want.on = 0;
      at.x = at.y = at.on = 0;
      if(frame){ cancelAnimationFrame(frame); frame = 0; }
      lean.classList.remove('tracking');
      for(const [k, v] of [['--ty','0deg'],['--tx','0deg'],['--lift','0px'],['--pick','1'],
                           ['--glint','0'],
                           ['--sh-x','0'],['--sh-y','15'],['--sh-b','0'],['--sh-a','0']])
        lean.style.setProperty(k, v);
      for(const face of faces)
        for(const k of ['--wash','--spec']) face.el.style.setProperty(k, '0');
    });

    /* the finger's own gestures: down starts a lean, movement is a drag, and a lift with no travel is a tap */
    hit.addEventListener('pointerdown', e => {
      byTouch = !isMouse(e);
      if(isMouse(e) || still.matches) return;
      dragging = true; moved = 0; start = { x:e.clientX, y:e.clientY };
      hit.setPointerCapture && hit.setPointerCapture(e.pointerId);
      hit.dispatchEvent(new PointerEvent('pointermove', e));
    });
    hit.addEventListener('pointermove', e => {
      if(dragging && start) moved = Math.max(moved, Math.hypot(e.clientX - start.x, e.clientY - start.y));
    });
    for(const end of ['pointerup','pointercancel'])
      hit.addEventListener(end, e => {
        if(isMouse(e)) return;
        dragging = false;
        hit.dispatchEvent(new Event('pointerleave'));
      });
    if(onClick) hit.addEventListener('click', e => {
      if(moved > 8){ moved = 0; return; }   // that was a drag, not a tap
      onClick(e);
    });
  }

  class NfGiftCard extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.data = JSON.parse(this.querySelector('[data-nf-gc-data]').textContent);
      } catch (e) {
        return;
      }
      this.format = this.dataset.format || 'egift';
      this.variantInput = this.querySelector('[data-nf-gc-variant]');
      this.priceEl = this.querySelector('[data-nf-gc-price]');
      this.addBtn = this.querySelector('[data-nf-gc-add]');
      this.amountInputs = [...this.querySelectorAll('[data-nf-gc-amount-input]')];
      this.formatInputs = [...this.querySelectorAll('[data-nf-gc-format]')];
      this.fieldsets = [...this.querySelectorAll('[data-nf-gc-for]')];
      this.writeAmounts = [...this.querySelectorAll('[data-nf-gc-amount]')];
      this.flatImg = this.querySelector('[data-nf-gc-flat-img]');
      this.views = [...this.querySelectorAll('[data-nf-gc-view]')];
      this.writeTo = this.querySelector('[data-nf-gc-to]');
      this.writeFrom = this.querySelector('[data-nf-gc-from]');
      this.recipientToggle = this.querySelector('[data-nf-gc-recipient-toggle]');
      this.recipientBox = this.querySelector('[data-nf-gc-recipient]');
      this.errorWrap = this.querySelector('.nf-gc__error');
      this.errorText = this.querySelector('[data-nf-gc-error]');

      const q = new URLSearchParams(window.location.search);
      const qFormat = q.get('format');
      if (qFormat === 'printed' || qFormat === 'egift') this.format = qFormat;
      if (!this.data.formats[this.format] || !this.data.formats[this.format].length) {
        this.format = this.format === 'egift' ? 'printed' : 'egift';
      }
      const qAmount = parseInt((q.get('amount') || '').replace(/\D/g, ''), 10);
      this.cents = qAmount > 0 ? qAmount * 100 : this.data.defaultCents;

      this.formatInputs.forEach((input) => {
        input.checked = input.value === this.format;
        input.addEventListener('change', () => {
          if (input.checked) this.setFormat(input.value);
        });
      });
      this.amountInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.cents = Number(input.value);
          this.update();
          if (this.data.turnOnWrite) this.turnTo('front');
        });
      });
      this.querySelectorAll('[data-nf-gc-write]').forEach((input) => {
        input.addEventListener('input', () => this.writeNames());
        input.addEventListener('focus', () => {
          if (this.data.turnOnWrite) this.turnTo('back');
        });
      });
      if (this.recipientToggle) {
        this.recipientToggle.addEventListener('change', () => this.setRecipient());
      }
      const date = this.querySelector('[data-nf-gc-date]');
      if (date) {
        const day = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        const now = new Date();
        date.min = day(now);
        date.max = day(new Date(now.getTime() + (this.data.maxSendDays || 90) * 86400000));
      }

      const form = this.querySelector('form');
      // capture: runs before Avante's own submit handler on the form
      this.querySelector('product-form')?.addEventListener('submit', (e) => this.check(e), true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (!form || String(event.productVariantId) !== String(this.variantInput.value)) return;
          this.showError(this.readError(event));
        });
      }

      this.setFormat(this.format, true);
      this.setRecipient();
      this.writeNames();
      this.setupCard();
    }

    variants() {
      return this.data.formats[this.format] || [];
    }

    setFormat(format, first) {
      this.format = format;
      this.dataset.format = format;
      this.fieldsets.forEach((fs) => {
        fs.disabled = fs.dataset.nfGcFor !== format;
      });
      this.views.forEach((v) => {
        v.hidden = v.dataset.nfGcView !== format;
      });
      this.update();
      this.writeNames();
      if (!first) this.clearError();
    }

    update() {
      const list = this.variants();
      const byCents = new Map(list.map((v) => [v.cents, v]));
      // amounts this format has; one it lacks or has sold out is struck through and cannot be chosen
      this.amountInputs.forEach((input) => {
        const v = byCents.get(Number(input.value));
        input.disabled = !v || !v.available;
        const mark = input.parentElement.querySelector('[data-nf-gc-soldout]');
        if (mark) mark.hidden = !(v && !v.available);
      });
      let pick = byCents.get(this.cents);
      if (!pick || !pick.available) {
        pick = list.find((v) => v.available && v.cents === this.data.defaultCents) || list.find((v) => v.available) || pick;
      }
      if (pick) this.cents = pick.cents;
      this.amountInputs.forEach((input) => {
        input.checked = pick ? Number(input.value) === pick.cents : false;
      });
      this.variantInput.value = pick ? pick.id : '';
      this.variantInput.disabled = !pick;
      const can = !!(pick && pick.available);
      if (this.addBtn) {
        this.addBtn.toggleAttribute('disabled', !can);
        this.addBtn.setAttribute('aria-disabled', String(!can));
        const label = this.addBtn.querySelector('[data-nf-gc-add-label]');
        const soldOut = this.addBtn.querySelector('.sold-out-message');
        if (label) label.hidden = !can;
        if (soldOut) soldOut.hidden = can;
      }
      if (this.priceEl) this.priceEl.textContent = pick ? pick.price : '';
      this.writeAmounts.forEach((el) => {
        el.textContent = pick ? String(Math.round(pick.cents / 100)) : '';
      });
      if (this.flatImg && pick && this.format === 'egift' && pick.img) {
        this.flatImg.src = pick.img;
        this.flatImg.srcset = `${pick.img} 720w, ${pick.img2 || pick.img} 1440w`;
      }
    }

    writeNames() {
      if (!this.writeTo) return;
      const box = this.querySelector(`[data-nf-gc-for="${this.format}"]`);
      const val = (key) => {
        const input = box && box.querySelector(`[data-nf-gc-write="${key}"]`);
        return input && !input.disabled ? input.value.trim() : '';
      };
      this.writeTo.textContent = val('to');
      this.writeFrom.textContent = val('from');
    }

    setRecipient() {
      if (!this.recipientToggle || !this.recipientBox) return;
      const on = this.recipientToggle.checked;
      this.recipientBox.hidden = !on;
      this.recipientBox.querySelectorAll('input, textarea').forEach((el) => {
        el.disabled = !on;
      });
      this.writeNames();
    }

    check(e) {
      this.clearError();
      const box = this.querySelector(`[data-nf-gc-for="${this.format}"]`);
      const email = box && !box.disabled ? box.querySelector('input[type="email"]:not(:disabled)') : null;
      if (email) {
        email.value = email.value.trim();
        const ok = email.value !== '' && email.checkValidity();
        email.setAttribute('aria-invalid', String(!ok));
        const msg = this.querySelector(`#${email.getAttribute('aria-describedby')}`);
        if (msg) msg.hidden = ok;
        if (!ok) {
          e.preventDefault();
          e.stopImmediatePropagation();
          email.focus();
          return;
        }
      }
      if (this.variantInput.disabled || !this.variantInput.value) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }

    readError(event) {
      const d = event && (event.errors || event.message);
      if (!d) return '';
      if (typeof d === 'string') return d;
      return Object.values(d).flat().join(' ');
    }

    showError(text) {
      if (!this.errorWrap || !text) return;
      this.errorText.textContent = text;
      this.errorWrap.hidden = false;
    }

    clearError() {
      if (this.errorWrap) this.errorWrap.hidden = true;
      this.querySelectorAll('.nf-gc__field-error').forEach((el) => {
        el.hidden = true;
      });
      this.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.setAttribute('aria-invalid', 'false'));
    }

    /* ------------------------------------------------------------ the card */
    // the app's onClick: liveAll(); side = other side; render()
    turnTo(side) {
      if (!this.lean) return;
      if (side === this.dataset.side) return;
      liveAll(this.lean);
      this.dataset.side = side;
      const back = side === 'back';
      this.querySelector('[data-nf-gc-turn]')?.setAttribute('aria-pressed', String(back));
      this.querySelector('.nfc-plate-front')?.setAttribute('aria-hidden', String(back));
      this.querySelector('.nfc-plate-back')?.setAttribute('aria-hidden', String(!back));
    }

    setupCard() {
      this.dataset.side = 'front';
      const hit = this.querySelector('[data-nf-gc-hit]');
      this.lean = this.querySelector('[data-nf-gc-tilt]');
      if (!hit || !this.lean) return;
      const flip = () => this.turnTo(this.dataset.side === 'front' ? 'back' : 'front');
      this.querySelector('[data-nf-gc-turn]')?.addEventListener('click', flip);
      if (!this.data.tilt) {
        hit.addEventListener('click', flip);
        return;
      }
      makeLean({
        hit, lean: this.lean,
        faces: [...this.querySelectorAll('.nfc-plate-front .nfc-card')].map(el => ({ el, sign: 1 })).concat(
               [...this.querySelectorAll('.nfc-plate-back .nfc-card')].map(el => ({ el, sign: -1 }))),
        onClick: flip
      });
    }
  }

  customElements.define('nf-gift-card', NfGiftCard);
})();
